import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';

const DependencyTreeView = () => {
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    const fetchTreeData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await authGet('/api/common-library/tree');
        
        if (!isMounted) return;
        
        if (response && response.success) {
          if (response.hasTree) {
            setTreeData(response);
            // Expand root node by default
            if (response.rootNode) {
              setExpandedNodes(new Set([response.rootNode.id]));
            }
          } else {
            setError(response.message || 'No dependency tree has been uploaded yet');
          }
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching dependency tree:', err);
        setError(err.message || 'Failed to fetch dependency tree');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchTreeData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    if (!treeData || !treeData.rootNode) return;
    
    const allNodeIds = new Set();
    const collectNodeIds = (node) => {
      allNodeIds.add(node.id);
      if (node.children) {
        node.children.forEach(collectNodeIds);
      }
    };
    collectNodeIds(treeData.rootNode);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    if (!treeData || !treeData.rootNode) return;
    setExpandedNodes(new Set([treeData.rootNode.id]));
  };

  const matchesSearch = (node, searchTerm) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const nodeText = `${node.group}:${node.name}:${node.version}`.toLowerCase();
    return nodeText.includes(searchLower);
  };

  const filterTree = (node, searchTerm) => {
    if (!searchTerm) return node;
    
    const matches = matchesSearch(node, searchTerm);
    const filteredChildren = node.children
      ? node.children
          .map(child => filterTree(child, searchTerm))
          .filter(child => child !== null)
      : [];

    if (matches || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren
      };
    }
    return null;
  };

  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const displayName = `${node.group}:${node.name}:${node.version}`;
    const indent = level * 24;

    return (
      <React.Fragment key={node.id}>
        <ListItem
          disablePadding
          sx={{
            pl: `${indent}px`,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ListItemButton
            onClick={() => hasChildren && toggleNode(node.id)}
            sx={{ py: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {hasChildren ? (
                isExpanded ? (
                  <ExpandLessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
                )
              ) : (
                <Box sx={{ width: 24, mr: 1 }} />
              )}
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      fontWeight: level === 0 ? 600 : 'normal'
                    }}
                  >
                    {displayName}
                  </Typography>
                }
                secondary={
                  node.scope && (
                    <Typography variant="caption" color="text.secondary">
                      Scope: {node.scope}
                    </Typography>
                  )
                }
              />
            </Box>
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children.map(child => renderTreeNode(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!treeData || !treeData.rootNode) {
    return (
      <Box>
        <Alert severity="info">
          No dependency tree uploaded yet. Upload a dependencies.json file to view the tree.
        </Alert>
      </Box>
    );
  }

  const filteredRoot = filterTree(treeData.rootNode, searchTerm);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Dependency Tree
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {treeData.allCommonLibraries?.length || 0} libraries
        </Typography>
      </Box>

      {treeData.rootDependency && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Root: {treeData.rootDependency}
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1} alignItems="center" mb={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search dependencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <IconButton size="small" onClick={expandAll} title="Expand All">
            <ExpandLessIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={collapseAll} title="Collapse All">
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
        {treeData.lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            Updated: {(() => {
              try {
                return new Date(treeData.lastUpdated).toLocaleString();
              } catch (e) {
                return new Date(treeData.lastUpdated).toString();
              }
            })()}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ maxHeight: '60vh', overflow: 'auto' }}>
        <List>
          {filteredRoot ? (
            renderTreeNode(filteredRoot)
          ) : (
            <ListItem>
              <ListItemText>
                <Typography variant="body2" color="text.secondary">
                  No dependencies match your search.
                </Typography>
              </ListItemText>
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default DependencyTreeView;

